import { subclass, property } from "@arcgis/core/core/accessorSupport/decorators";
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";
import { VNode } from 'src/interfaces/interfaces';

import t9nAppMessages from "../../t9n/App/App.json";
import ContentAreaViewModel from '../ui/ContentArea/ContentAreaViewModel';
import "./DeleteModal/DeleteModal.scss";

export interface DeleteModalProperties extends __esri.WidgetProperties {
  appInfo: __esri.PortalItem;
  orgUrl: string;
  contentAreaVM: ContentAreaViewModel;
}

@subclass("Landing.widgets.DeleteModal")
export default class DeleteModal extends Widget {

  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  appInfo: __esri.PortalItem;

  @property()
  orgUrl: string;

  @property()
  contentAreaVM: ContentAreaViewModel;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof t9nAppMessages;

  @property()
  modalVisible: boolean = false;

  @property()
  _modalDOMContent: VNode[] = [];
  @property()
  _canDelete: boolean;
  @property()
  _isDeleting: boolean = false;
  @property()
  _isQueryingForCanDelete: boolean;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  constructor(props: DeleteModalProperties) {
    super(props);
  }

  render() {
    const { close, deleteLabel } = this.appMessages.myApps.delete;
    return (
      <calcite-modal
        active={this.modalVisible}
        color="red"
        background-color="white"
        scale="m"
        width="s"
        intl-close={close}
        afterCreate={(modal: HTMLCalciteModalElement) => {
          modal.addEventListener("calciteModalClose", () => {
            this.hideModal();
          });
        }}
      >
        <h1 slot="header">{deleteLabel}</h1>

        <div slot="content">
          {this._renderContentArea()}
        </div>
        <div slot="secondary">
          {this._renderSecondaryBtns()}
        </div>
        <div slot="primary">
          {this._renderPrimaryBtns()}
        </div>
      </calcite-modal>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------
  showModal(): void {
    this.modalVisible = true;
    this._isQueryingForCanDelete = true;
    this._queryForCanDelete().then(() => {
      setTimeout(() => { // very slight delay to ease the calcite-loader out
        this._isQueryingForCanDelete = false;
      }, 700);
    });
  }

  hideModal(): void {
    this.modalVisible = false;
  }

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private _renderContentArea(): VNode {
    if (this._isQueryingForCanDelete) {
      return (
        <calcite-loader active={true}></calcite-loader>
      );
    } else {
      return (
        !this._canDelete
          ? this._renderDeleteProtection()
          : this._renderDeleteConfirmation()
      )
    }
  }

  private _renderSecondaryBtns(): VNode {
    const { cancel } = this.appMessages.myApps.delete;
    if (this._isQueryingForCanDelete) {
      return (
        <calcite-button
          key="delete_cancelBtn_fake"
          scale="l"
          class="placeholder-button"
        > {cancel}
        </calcite-button>
      );
    } else {
      return (
        <calcite-button
          key="delete_cancelBtn"
          width="full"
          appearance="outline"
          color="red"
          alignment="center"
          scale="l"
          onclick={this.hideModal.bind(this)}
        >
          {cancel}
        </calcite-button>
      )
    }
  }

  private _renderPrimaryBtns(): VNode {
    const { deleteLabel } = this.appMessages.myApps.delete;
    if (this._isQueryingForCanDelete) {
      return (
        <calcite-button
          key="delete_deleteBtn_fake"
          scale="l"
          class="placeholder-button"
        >{deleteLabel}
        </calcite-button>
      );
    } else {
      return (
        this._canDelete && (
          <calcite-button
            key="delete_deleteBtn"
            width="full"
            appearance="solid"
            color="red"
            alignment="center"
            scale="l"
            loading={this._isDeleting}
            onclick={this._performDelete.bind(this)}
          >
            { deleteLabel}
          </calcite-button>
        )
      )
    }
  }

  private async _queryForCanDelete() {
    const deleteCheckRes = await this.contentAreaVM.publishAppVM.checkCanDelete(this.appInfo.id);
    this._canDelete = deleteCheckRes.success;
    return this._canDelete;
  }

  private _renderDeleteProtection() {
    const { deleteProtectionMsg } = this.appMessages.myApps.delete;
    const { id, title } = this?.appInfo;

    return (
      <div key="canDeleteFalse">
        <p> {deleteProtectionMsg} </p>
        <p>
          <calcite-link
            icon-start="launch"
            href={`${this.orgUrl}/home/item.html?id=${id}#settings`}
            target="_blank"
          >
            {title}
          </calcite-link>
        </p>
      </div>
    )
  }

  private _renderDeleteConfirmation() {
    const { deleteConfirmMsg } = this.appMessages.myApps.delete;
    return (
      <div key="canDeleteTrue">
        <p>
          {deleteConfirmMsg}
        </p>
      </div>
    )
  }

  private _performDelete(): void {
    const { deleteSuccess, deleteFailed } = this.appMessages.myApps.delete;
    const { id, title } = this.appInfo;

    this._isDeleting = true;
    this.contentAreaVM.publishAppVM.deleteItem(id).then((deleteRes) => {
      this._isDeleting = false;
      this.hideModal();
      if (deleteRes === true) {
        this.contentAreaVM.showAlert(
          deleteSuccess.title,
          `"${title}" ${deleteSuccess.msg}`,
          "success"
        );
        this.contentAreaVM.myAppsDataVM.removeFromMyAppsOnDelete(id);

      } else {
        this.contentAreaVM.showAlert(
          deleteFailed.title,
          `${deleteFailed.msg1}: ${title} ${deleteFailed.msg2}. ${deleteRes !== false && deleteRes}`,
          "error"
        );
      }
    });
  }


}